import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'
import { collection, onSnapshot, query, Unsubscribe, where } from 'firebase/firestore'

import { HorseAccordionWrapper } from '../../../../../../pages/organizer/exhibitorProfile/HorseAccordionWrapper'
import { SimpleEmpty } from '../../empty/SimpleEmpty/SimpleEmpty'
import { ChevronDownIcon } from 'lucide-react'
import { Button } from '../../../../../ui/button'

import { getRidersByPaperwork, isValidPaperwork } from '../../../../../../helpers/paperwork'
import { getConvertedData } from '../../../../../../models/interface.helper'
import {
  getRiderTeamMembers,
  haveTeamMember,
  isValidTeamMember,
} from '../../../../../../helpers/teamMembers'
import { cn } from '../../../../../../lib/utils'

import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'
import FirebaseApp from '../../../../../../services/firebaseApp'
import RiderTeamMemberService from '../../../../../../services/firebase-service/rider-team-member/RiderTeamMemberService'
import EventReviewPublishService from '../../../../../../services/firebase-service/event-review-publish/EventReviewPublishService'

import { RiderTeamMemberModel } from '../../../../../../models/rider-team-member/riderTeamMember.model'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IUserDocument } from '../../../../../../models/user-documents/user-documents.interface'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'
import { IRiderTeamMemberInterface } from '../../../../../../models/rider-team-member/riderTeamMember.interface'
import { UserDocumentModel } from '../../../../../../models/user-documents/user-documents.model'

import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { CONST } from '../../../../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

interface PaperworkTabProps {
  type?: 'overview' | 'registrar'
  className?: string
  isCollapsible?: boolean
  registrationsByDay: IRegistrationByDayInterface[]
  eventId: string
  userId: string
  registrationId: string
  onActionClick?: () => void
  actionIcon?: ReactNode
  isLoading?: boolean
}

export const PaperworkTab: FC<PaperworkTabProps> = ({
  type,
  className,
  isCollapsible,
  registrationsByDay,
  eventId,
  userId,
  registrationId,
  onActionClick,
  actionIcon,
  isLoading,
}) => {
  const [loading, setLoading] = useState(true)
  const [filteredRiders, setFilteredRiders] = useState<IRegistrationByDayInterface[]>([])
  const [isOpen, setIsOpen] = useState(!isCollapsible)
  const [eventReviewPublishInDb, setEventReviewPublishInDb] = useState<IEventReviewPublish | null>(
    null
  )
  const [usersDocuments, setUsersDocuments] = useState<IUserDocument[]>([])
  const [riderTeamMembers, setRiderTeamMembers] = useState<IRiderTeamMemberInterface[]>([])
  const [signMissing, setSignMissing] = useState(0)

  const handleModalContext = useContext(HandleModalContext)

  useEffect(() => {
    let filtered = getRidersByPaperwork(registrationsByDay)

    if (type === 'overview') {
      filtered = filtered.filter((rider) => {
        const teamMembers = getRiderTeamMembers(rider, riderTeamMembers, usersDocuments)
        return isValidPaperwork(teamMembers)
      })
    }

    setFilteredRiders(filtered)
  }, [riderTeamMembers, registrationsByDay, type, usersDocuments])

  useEffect(() => {
    const allRiders = getRidersByPaperwork(registrationsByDay)
    let paperworkIssues = 0
    allRiders.forEach((rider) => {
      const teamMembers = getRiderTeamMembers(rider, riderTeamMembers, usersDocuments)

      const haveRider = haveTeamMember(teamMembers, CONST.UI.TEAM_MEMBERS_ROLES.RIDER)
      if (!haveRider) paperworkIssues += 1

      paperworkIssues += isValidTeamMember(teamMembers)
    })

    setSignMissing(paperworkIssues)
  }, [riderTeamMembers, registrationsByDay, usersDocuments])

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true)

        const riderTeamMembersInDb = await RiderTeamMemberService.getByRider(registrationId, userId)
        const eventReviewPublishInDb = await EventReviewPublishService.getById(eventId)

        setEventReviewPublishInDb(eventReviewPublishInDb)
        setRiderTeamMembers(riderTeamMembersInDb)
      } catch (error) {
        console.error(error, 'error')
      } finally {
        setLoading(false)
      }
    }

    if (!isLoading) getData().then()
  }, [eventId, registrationId, userId, registrationsByDay, isLoading])

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    if (userId && registrationId) {
      const ref = collection(FirebaseApp.firestore, COLLECTIONS.USERS_DOCUMENTS.NAME)
      const q = query(
        ref,
        where(COLLECTIONS.USERS_DOCUMENTS.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        where(COLLECTIONS.USERS_DOCUMENTS.FIELDS.RIDER_ID.KEY, '==', userId)
      )

      unsubscribe = onSnapshot(q, (snaps) => {
        const userDocumentsUpdated: IUserDocument[] = []
        snaps.docs.forEach((doc) => {
          const current = getConvertedData(UserDocumentModel.fromFirestoreDoc(doc).toObject())
          userDocumentsUpdated.push(current)
        })
        setUsersDocuments(userDocumentsUpdated)
      })
    }

    return () => {
      unsubscribe?.()
    }
  }, [registrationId, userId])

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    if (userId && registrationId) {
      const ref = collection(FirebaseApp.firestore, COLLECTIONS.RIDER_TEAM_MEMBER.NAME)
      const q = query(
        ref,
        where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.REGISTRATION_DOC_ID.KEY, '==', registrationId),
        where(COLLECTIONS.RIDER_TEAM_MEMBER.FIELDS.RIDER_ID.KEY, '==', userId)
      )

      unsubscribe = onSnapshot(q, (snaps) => {
        const riderTeamMembersUpdated: IRiderTeamMemberInterface[] = []
        snaps.docs.forEach((doc) => {
          const current = getConvertedData(RiderTeamMemberModel.fromFirestoreDoc(doc).toObject())
          riderTeamMembersUpdated.push(current)
        })
        setRiderTeamMembers(riderTeamMembersUpdated)
      })
    }

    return () => {
      unsubscribe?.()
    }
  }, [registrationId, userId])

  if (loading || isLoading) return <SimpleEmpty />

  return (
    <div
      className={cn(
        (type === 'overview' || type === 'registrar') &&
          'rounded-lg border border-black/10 px-7 pt-3',
        !isOpen && 'pb-3',
        !filteredRiders?.[0] && 'py-3',
        className
      )}
    >
      {(type === 'overview' || type === 'registrar') && (
        <div className="flex gap-2 flex-wrap items-center justify-between">
          <div>
            <p className="text-base font-medium">Paperwork</p>
            <p className="text-xs text-black/50">
              {filteredRiders?.[0] ? `${signMissing} issues` : 'No issues'}
            </p>
          </div>
          <div className="text-right flex gap-3 items-center justify-end ml-auto">
            <Button
              variant="link"
              type="button"
              className="rounded-full p-0"
              onClick={onActionClick}
            >
              {actionIcon}
            </Button>
            {isCollapsible && (
              <Button
                variant="link"
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                  'rounded-full transition-all duration-200 w-[30px] h-[30px] flex items-center justify-center shrink-0',
                  isOpen
                    ? 'text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rotate-180'
                    : 'text-black bg-black/5'
                )}
              >
                <ChevronDownIcon className="size-4" />
              </Button>
            )}
          </div>
        </div>
      )}
      {isOpen && (
        <div
          className={cn(
            (isCollapsible || type === 'overview') &&
              filteredRiders?.[0] &&
              'mt-3 border-t border-t-black/10'
          )}
        >
          {filteredRiders.map((item, index) => {
            const teamMembers = getRiderTeamMembers(item, riderTeamMembers, usersDocuments)

            return (
              <HorseAccordionWrapper
                key={index}
                type={type}
                item={item}
                teamMembers={teamMembers}
                addSignerHandler={(defaultTeamMemberId, defaultRole, onlyPaperwork?: boolean) => {
                  handleModalContext?.handleModal?.(
                    true,
                    MODAL_CONSTS.ADD_TEAM_MEMBER_TO_PAPERWORK_STEP_1,
                    {
                      ...handleModalContext?.dataToPassOn,
                      riderTeamMembers: riderTeamMembers,
                      registrationByDay: item,
                      defaultRole: defaultRole,
                      defaultTeamMemberId: defaultTeamMemberId,
                      onlyPaperwork: onlyPaperwork || false,
                    }
                  )
                }}
                eventReviewPublish={eventReviewPublishInDb}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
